import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trash2,
  Plus,
  Minus,
  ArrowLeft,
  ShoppingBag,
  ShieldCheck,
  Tag,
  Gift,
  ArrowRight,
  Sparkles,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import API from '../config/api';
import axios from 'axios';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showCouponsList, setShowCouponsList] = useState(false);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await axios.get(`${API}/coupons/active`);
        if (Array.isArray(res.data)) {
          setAvailableCoupons(res.data);
        }
      } catch (err) {
        console.error('Failed to load active coupons:', err);
      }
    };
    fetchCoupons();
  }, []);

  const handleApplyCoupon = async (codeToApply) => {
    const code = (codeToApply || couponCode).trim();
    if (!code) return;

    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: code,
        cart_total: cartTotal
      });
      setCouponApplied(res.data);
      setCouponCode(res.data.code);
      toast.success(res.data.message || `Coupon ${res.data.code} applied!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon');
      setCouponApplied(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleCheckout = () => {
    if (!user) {
      toast.info('Please log in or register to complete your order.');
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout', { state: { appliedCoupon: couponApplied } });
  };

  const deliveryThreshold = 999;
  const progressToFree = Math.min((cartTotal / deliveryThreshold) * 100, 100);
  const discountAmount = couponApplied?.discount || 0;
  const shippingFee = cartTotal >= deliveryThreshold ? 0 : 99;
  const finalTotal = Math.max(0, cartTotal - discountAmount + shippingFee);

  return (
    <div className="bg-[#FAF7F0] min-h-screen flex flex-col text-[#2A1F16]">
      <Navbar />

      <main className="flex-1 pt-20 sm:pt-24 pb-12 relative overflow-hidden">
        {/* Subtle Ambient Glow */}
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#E8D5A8]/20 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
        <div className="absolute bottom-10 right-10 w-[25rem] h-[25rem] bg-[#6E1B34]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
            <div>
              <button
                onClick={() => navigate('/store')}
                className="inline-flex items-center space-x-1.5 text-[#83725B] hover:text-[#4E1225] mb-2.5 transition-colors text-xs font-semibold uppercase tracking-wider bg-white/80 px-3 py-1.5 rounded-full border border-[#E7D9BC] shadow-2xs group"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                <span>Continue Shopping</span>
              </button>
              <h1 className="font-serif-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#4E1225] tracking-tight">
                Your <span className="italic text-[#6E1B34]">Sacred Cart</span>
              </h1>
            </div>

            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="text-[#83725B] hover:text-red-600 text-xs font-semibold transition-colors px-2.5 py-1 rounded-md hover:bg-red-50 w-max"
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </button>
            )}
          </div>

          {cart.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/80 backdrop-blur-sm border border-[#E7D9BC] rounded-2xl p-8 sm:p-14 text-center shadow-xs"
            >
              <div className="w-16 h-16 mx-auto bg-[#FBF4E7] rounded-full flex items-center justify-center mb-4 border border-[#E7D9BC]">
                <ShoppingBag className="w-7 h-7 text-[#6E1B34]" />
              </div>
              <h2 className="font-serif-heading text-xl sm:text-2xl font-bold text-[#4E1225] mb-2">
                Your sacred cart is empty
              </h2>
              <p className="text-sm text-[#83725B] mb-6 max-w-sm mx-auto">
                Explore authentic Himalayan prasad, energized rudrakshas, pure Badri cow ghee, and sacred temple pujas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/store">
                  <Button className="rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white font-bold text-sm px-6 py-2.5 shadow-sm transition-all">
                    Explore Store
                  </Button>
                </Link>
                <Link to="/temples">
                  <Button variant="outline" className="rounded-md border-[#6E1B34] text-[#6E1B34] hover:bg-[#6E1B34] hover:text-white font-semibold text-sm px-6 py-2.5 transition-all">
                    Book Pujas
                  </Button>
                </Link>
              </div>
            </motion.div>
          ) : (
            <div className="grid lg:grid-cols-12 gap-6 items-start">
              
              {/* Left Column: Cart Items List */}
              <div className="lg:col-span-7 xl:col-span-8 space-y-3">
                <AnimatePresence>
                  {cart.map((item, index) => {
                    const lineKey = item.cartItemId || item.id;
                    return (
                      <motion.div
                        key={lineKey}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ delay: index * 0.04 }}
                        className="bg-white rounded-2xl p-3.5 sm:p-4 shadow-2xs border border-[#E7D9BC] flex flex-col sm:flex-row gap-4 relative group hover:border-[#B98B3E] transition-all"
                        data-testid={`cart-item-${lineKey}`}
                      >
                        {/* Thumbnail */}
                        <div className="w-full sm:w-20 h-20 rounded-xl overflow-hidden bg-[#FBF4E7] flex-shrink-0 border border-[#E7D9BC]/60">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>

                        {/* Details */}
                        <div className="flex-1 flex flex-col justify-center min-w-0">
                          <h3 className="font-serif-heading text-base font-bold text-[#4E1225] truncate pr-6">
                            {item.name}
                          </h3>
                          {item.origin && (
                            <p className="text-xs text-[#83725B] flex items-center gap-1.5 mt-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#D9711F]"></span>
                              <span>{item.origin}</span>
                            </p>
                          )}

                          {/* Selected Addons Badges */}
                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {item.selectedAddons.map((ad, adIdx) => (
                                <span key={adIdx} className="inline-flex items-center gap-1 text-[10px] bg-[#FAF7F0] text-[#6E1B34] border border-[#E7D9BC] px-2 py-0.5 rounded-full font-medium">
                                  <Sparkles className="w-2.5 h-2.5 text-[#D9711F]" />
                                  <span>{ad.name} (+₹{ad.price})</span>
                                </span>
                              ))}
                            </div>
                          )}

                          <span className="font-bold text-[#6E1B34] text-sm mt-1">
                            ₹{item.price.toLocaleString()}
                          </span>
                        </div>

                        {/* Controls */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between border-t sm:border-t-0 pt-2.5 sm:pt-0 border-gray-100">
                          <button
                            onClick={() => removeFromCart(lineKey)}
                            className="text-gray-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors absolute top-3 right-3 sm:relative sm:top-0 sm:right-0"
                            data-testid={`remove-item-${lineKey}`}
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          
                          <div className="flex items-center bg-[#FBF4E7] border border-[#E7D9BC] rounded-lg p-0.5 mt-auto">
                            <button
                              onClick={() => updateQuantity(lineKey, item.quantity - 1)}
                              className="w-6 h-6 flex items-center justify-center rounded hover:bg-white text-gray-600 transition-all"
                              data-testid={`decrease-qty-${lineKey}`}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center font-bold text-xs text-[#4E1225]">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(lineKey, item.quantity + 1)}
                              className="w-6 h-6 flex items-center justify-center rounded hover:bg-white text-gray-600 transition-all"
                              data-testid={`increase-qty-${lineKey}`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {/* Gift callout banner */}
                <div className="bg-[#FBF4E7] border border-[#E7D9BC] rounded-xl p-3.5 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-[#6E1B34] text-white flex items-center justify-center flex-shrink-0">
                      <Gift className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <span className="font-bold text-[#4E1225]">Sending as a Sacred Gift?</span>
                      <p className="text-[#83725B] text-[11px]">Add personalized blessing message & auspicious packaging on the next step.</p>
                    </div>
                  </div>
                  <Sparkles className="w-4 h-4 text-[#D9711F] flex-shrink-0" />
                </div>
              </div>

              {/* Right Column: Order Summary */}
              <div className="lg:col-span-5 xl:col-span-4">
                <motion.div
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-2xl p-5 shadow-xs border border-[#E7D9BC] sticky top-24 space-y-4"
                >
                  <h3 className="font-serif-heading text-lg font-bold text-[#4E1225] pb-2 border-b border-[#E7D9BC]/50">
                    Order Summary
                  </h3>
                  
                  {/* Delivery Bar */}
                  <div className="p-3 bg-[#FBF4E7] rounded-xl border border-[#E7D9BC]/60 text-xs">
                    <div className="flex justify-between items-end mb-1.5">
                      <span className="font-semibold text-[#83725B]">Delivery</span>
                      <span className="font-bold text-[#4E1225]">
                        {cartTotal >= deliveryThreshold ? 'Free Shipping Unlocked! 🎉' : `Add ₹${(deliveryThreshold - cartTotal).toLocaleString()} for Free Delivery`}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-white rounded-full overflow-hidden border border-[#E7D9BC]">
                      <div 
                        className="h-full bg-[#6E1B34] transition-all duration-500"
                        style={{ width: `${progressToFree}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Coupon Box */}
                  <div className="border border-[#E7D9BC] bg-[#FBF4E7]/60 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[#6E1B34]">
                        <Tag className="w-3.5 h-3.5 text-[#D9711F]" />
                        <span>Promo Code</span>
                      </div>
                      {availableCoupons.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowCouponsList(!showCouponsList)}
                          className="text-xs text-[#D9711F] font-semibold hover:underline"
                        >
                          {showCouponsList ? 'Hide Offers' : 'View Offers'}
                        </button>
                      )}
                    </div>

                    {couponApplied ? (
                      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 text-xs">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-800">
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span>{couponApplied.code}</span>
                          <span className="text-emerald-600 font-normal">(-₹{couponApplied.discount})</span>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-[11px] font-bold text-red-600 hover:text-red-800 uppercase"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1.5">
                        <Input
                          placeholder="e.g. WELCOME10"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="bg-white uppercase text-xs font-mono h-8 rounded-lg border-[#E7D9BC]"
                        />
                        <Button
                          onClick={() => handleApplyCoupon()}
                          disabled={applyingCoupon || !couponCode.trim()}
                          className="bg-[#6E1B34] hover:bg-[#4E1225] text-white rounded-lg px-3 text-xs font-bold h-8"
                        >
                          {applyingCoupon ? '...' : 'Apply'}
                        </Button>
                      </div>
                    )}

                    {showCouponsList && availableCoupons.length > 0 && (
                      <div className="space-y-1.5 pt-1.5 border-t border-[#E7D9BC]/60">
                        {availableCoupons.map((c) => (
                          <div
                            key={c.code}
                            className="bg-white p-2 rounded-lg border border-[#E7D9BC] flex items-center justify-between text-xs"
                          >
                            <div>
                              <span className="font-bold text-[#6E1B34] font-mono bg-[#FBF4E7] px-1 py-0.5 rounded">
                                {c.code}
                              </span>
                              <p className="text-[#83725B] text-[10px] mt-0.5">{c.description || `Save with ${c.code}`}</p>
                            </div>
                            <button
                              onClick={() => handleApplyCoupon(c.code)}
                              className="text-xs font-bold text-[#D9711F] hover:underline"
                            >
                              Apply
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Summary Breakdown */}
                  <div className="space-y-2 text-xs text-[#83725B]">
                    <div className="flex justify-between">
                      <span>Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
                      <span className="font-bold text-[#2A1F16]">₹{cartTotal.toLocaleString()}</span>
                    </div>

                    {discountAmount > 0 && (
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>Coupon Discount ({couponApplied.code})</span>
                        <span>-₹{discountAmount.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-start">
                      <div>
                        <span>Estimated Shipping</span>
                        <p className="text-[10px] text-[#6E1B34] font-medium">
                          Choose Standard / Express / Priority courier at Checkout
                        </p>
                      </div>
                      <span className={shippingFee === 0 ? 'text-emerald-700 font-bold' : 'font-bold text-[#2A1F16]'}>
                        {shippingFee === 0 ? 'FREE' : `₹${shippingFee}`}
                      </span>
                    </div>

                    <div className="h-px bg-[#E7D9BC]/60 my-1.5"></div>

                    <div className="flex justify-between items-baseline pt-1">
                      <div>
                        <span className="font-bold text-[#4E1225] text-sm">Estimated Total</span>
                        <p className="text-[10px] text-[#83725B]">All taxes included</p>
                      </div>
                      <span className="font-price font-bold text-xl text-[#6E1B34]">
                        ₹{finalTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={handleCheckout}
                    className="w-full rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white py-3 px-5 text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    <span>Proceed to Checkout</span>
                    <ArrowRight className="w-4 h-4 text-[#E8D5A8]" />
                  </Button>

                  {!user && (
                    <p className="text-center text-[11px] text-[#83725B]">
                      🔒 Log in or sign up required at checkout
                    </p>
                  )}

                  <div className="flex items-center justify-center gap-1.5 text-[#83725B] text-[11px] pt-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#2E7D45]" />
                    <span>Safe & Encrypted 256-Bit Checkout</span>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
